import { useState, useEffect, useCallback } from 'react';
import { logger } from '../../../lib/utils';
import axios from 'axios';
import { isMobileDevice } from '../../../lib/utils';
import { API } from '../challengeUtils';
import { toast } from 'sonner';
import { isProfane, sanitizeName } from '../../../lib/profanityFilter';

/**
 * Custom hook for challenge creation & sharing.
 * Simplified: no more download/copy prerequisites — sharing is one-click via OG tags.
 * Notifications are auto-enabled silently without prompting the user.
 */
export function useChallengeCreation({
  isOpen,
  onClose,
  initialPlayerName,
  imageUrl,
  difficulty,
  scoreData,
  time,
  moves,
  deviceId,
  pushNotifications
}) {
  const [step, setStep] = useState('creating');
  const [playerName, setPlayerName] = useState(initialPlayerName || '');
  const [isCreating, setIsCreating] = useState(false);
  const [challengeCode, setChallengeCode] = useState(null);
  const [scorecardReady, setScorecardReady] = useState(false);
  const [scorecardKey, setScorecardKey] = useState(0);
  const [scorecardError, setScorecardError] = useState(false);
  const [hasAutoCreated, setHasAutoCreated] = useState(false);

  // Reset state when modal closes, init name when it opens
  useEffect(() => {
    if (!isOpen) {
      setChallengeCode(null);
      setIsCreating(false);
      setScorecardReady(false);
      setScorecardKey(0);
      setScorecardError(false);
      setStep('creating');
      setHasAutoCreated(false);
    } else {
      const name = initialPlayerName || localStorage.getItem('jigsaw_player_name') || 'Player';
      setPlayerName(name);
    }
  }, [isOpen, initialPlayerName]);

  const createChallenge = useCallback(async (nameOverride) => {
    const rawName = nameOverride || playerName || 'Player';
    const name = sanitizeName(rawName) || 'Player';

    if (isProfane(name.trim())) {
      toast.error('Profanity Detected! - Try Again');
      onClose();
      return;
    }

    setStep('creating');
    setIsCreating(true);
    setScorecardReady(false);

    try {
      localStorage.setItem('jigsaw_player_name', name.trim());
      const isMobile = isMobileDevice();

      const response = await axios.post(`${API}/challenges?is_mobile=${isMobile}`, {
        image_url: imageUrl,
        difficulty,
        creator_name: name.trim(),
        creator_device_id: deviceId
      });
      const newCode = response.data.challenge_code;
      setChallengeCode(newCode);

      if (scoreData && time !== undefined && moves !== undefined) {
        try {
          await axios.post(`${API}/challenges/${newCode}/score?is_mobile=${isMobile}`, {
            player_name: name.trim(),
            time_seconds: time,
            moves,
            device_id: deviceId
          });
          try {
            await axios.post(`${API}/challenges/${newCode}/regenerate-scorecard?is_mobile=${isMobile}`);
            setScorecardKey(Date.now());
          } catch (e) {
            logger.error('Failed to regenerate scorecard:', e);
          }
        } catch (e) {
          logger.error('Failed to submit creator score:', e);
        }
      }
      setScorecardReady(true);

      const saved = JSON.parse(localStorage.getItem('jigsaw_my_challenges') || '[]');
      if (!saved.includes(newCode)) {
        saved.unshift(newCode);
        localStorage.setItem('jigsaw_my_challenges', JSON.stringify(saved.slice(0, 20)));
      }

      setStep('share');
      // Auto-enable notifications silently (no prompt)
      if (pushNotifications?.isSupported && !pushNotifications?.isSubscribed && pushNotifications?.subscribe) {
        pushNotifications.subscribe().catch(() => {
          // Silent fail - notifications are optional
        });
      }
    } catch (error) {
      logger.error('Failed to create challenge:', error);
      onClose();
    } finally {
      setIsCreating(false);
    }
  }, [playerName, imageUrl, difficulty, scoreData, time, moves, deviceId, pushNotifications, onClose]);

  // Auto-create challenge when modal opens
  useEffect(() => {
    if (isOpen && !hasAutoCreated && !challengeCode) {
      const name = initialPlayerName || localStorage.getItem('jigsaw_player_name') || 'Player';
      setPlayerName(name);
      setHasAutoCreated(true);
      createChallenge(name);
    }
  }, [isOpen, hasAutoCreated, challengeCode, initialPlayerName, createChallenge]);

  const handleScorecardError = useCallback(() => {
    logger.error('Score card image failed to load');
    setScorecardError(true);
  }, []);

  return {
    step,
    challengeCode,
    scorecardReady,
    scorecardKey,
    scorecardError,
    handleScorecardError
  };
}
